import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerTrigger, DrawerFooter, DrawerDescription } from '@/components/ui/drawer';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { useDevice } from '@/contexts/DeviceContext';
import { useMutation, useQuery } from 'convex/react';
import { api } from '../../api';
import { Search, Plus, Loader2, CheckCircle2 } from 'lucide-react';
import { useEffectiveSerial } from '@/hooks/useEffectiveSerial';
import { useItunesSearch } from '@/hooks/useItunesSearch';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';

const Requests: React.FC = () => {
  const { toast } = useToast();
  const { selectedDevice } = useDevice();
  const { serial, source } = useEffectiveSerial();

  console.log('Requests - Serial Resolution:', { serial, source });
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedApp, setSelectedApp] = useState<any>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isWebDrawerOpen, setIsWebDrawerOpen] = useState(false);
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [websiteReason, setWebsiteReason] = useState('');

  // iTunes Search
  const { results: appSearchResults, isLoading: isSearching, error: searchError } = useItunesSearch(searchQuery);

  // Fetch app requests for this device
  const appRequestsData = useQuery(
    api.requests.list.myAppRequests, 
    serial ? { serial, paginationOpts: { numItems: 20, cursor: null } } : "skip"
  );
  
  // Handle error state (e.g., device not found in backend)
  const hasDeviceError = appRequestsData instanceof Error;
  const appRequests = (!hasDeviceError && appRequestsData?.page) ? appRequestsData.page : [];

  // Mutations to create requests
  const makeRequest = useMutation(api.requests.create.appRequest);
  const makeWebRequest = useMutation(api.requests.create.webRequest);

  const getStatusBadgeVariant = (status?: string) => {
    switch (status) {
      case 'pending':
        return 'secondary';
      case 'deny':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  // Safely format request date using available timestamps
  const formatRequestDate = (req: any) => {
    const ts = req?._creationTime ?? req?.responseTime ?? req?.rejectedAt;
    if (!ts) return '';
    const d = new Date(Number(ts));
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const handleSubmitRequest = async () => {
    if (!selectedApp) {
      toast({
        title: "App Required",
        description: "Please select an app to request.",
        variant: "destructive",
      });
      return;
    }

    if (!serial) {
      toast({
        title: "Device Required",
        description: "No device serial available.",
        variant: "destructive",
      });
      return;
    }

    try {
      await makeRequest({
        app: selectedApp.bundleId || selectedApp.trackId.toString(),
        bundleId: selectedApp.bundleId || selectedApp.trackId.toString(),
        serial: serial,
        message: `Request for ${selectedApp.trackName} (Bundle ID: ${selectedApp.bundleId || 'N/A'})`,
        trackId: selectedApp.trackId.toString(),
        name: selectedApp.trackName,
        iconUrl: selectedApp.artworkUrl100,
        category: selectedApp.primaryGenreName,
        version: selectedApp.version
      } as any);

      toast({
        title: "Request Submitted",
        description: "Your app request has been submitted for review.",
        duration: 2000,
      });

      // Reset form and close drawer
      setSelectedApp(null);
      setSearchQuery('');
      setIsDrawerOpen(false);
    } catch (error) {
      console.error('Error submitting request:', error);
      toast({
        title: "Submission Failed",
        description: error instanceof Error ? error.message : "Failed to submit request.",
        variant: "destructive",
      });
    }
  };

  // Strip protocol from URL input
  const stripProtocol = (url: string) => {
    return url.replace(/^https?:\/\//i, '').trim();
  };

  const handleSubmitWebRequest = async () => {
    const cleanUrl = stripProtocol(websiteUrl);
    
    // Validate URL
    if (!cleanUrl) {
      toast({
        title: "Website Required",
        description: "Please enter a website domain.",
        variant: "destructive",
      });
      return;
    }

    // Basic domain validation (allow domains like example.com)
    const domainRegex = /^[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?(\.[a-zA-Z]{2,})+$/;
    if (!domainRegex.test(cleanUrl)) {
      toast({
        title: "Invalid Domain",
        description: "Please enter a valid domain (e.g., example.com).",
        variant: "destructive",
      });
      return;
    }

    if (!serial) {
      toast({
        title: "Device Required",
        description: "No device serial available.",
        variant: "destructive",
      });
      return;
    }

    try {
      await makeWebRequest({
        url: cleanUrl,
        serial: serial,
        message: websiteReason || `Request for website: ${cleanUrl}`
      });

      toast({
        title: "Request Submitted",
        description: "Your website request has been submitted for review.",
        duration: 2000,
      });

      // Reset form and close drawer
      setWebsiteUrl('');
      setWebsiteReason('');
      setIsWebDrawerOpen(false);
    } catch (error) {
      console.error('Error submitting web request:', error);
      toast({
        title: "Submission Failed",
        description: error instanceof Error ? error.message : "Failed to submit request.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="min-h-screen bg-muted/30 pb-32">
      {/* Header */}
      <div className="bg-background border-b border-border">
        <div className="px-5 pt-6 pb-4">
          <h1 className="text-[28px] font-bold tracking-tight text-foreground">Requests</h1>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-4 pt-6">
        <Tabs defaultValue="app" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="app">App Request</TabsTrigger>
            <TabsTrigger value="web">Web Request</TabsTrigger>
          </TabsList>

          {/* App Request Tab */}
          <TabsContent value="app" className="space-y-4">
            {/* New Request Button at Top */}
            <Drawer open={isDrawerOpen} onOpenChange={(open) => {
              setIsDrawerOpen(open);
              if (!open) {
                setSelectedApp(null);
                setSearchQuery('');
              }
            }}>
              <DrawerTrigger asChild>
                <Button className="w-full gap-2">
                  <Plus className="w-4 h-4" />
                  New App Request
                </Button>
              </DrawerTrigger>
              <DrawerContent className="h-[90vh] flex flex-col">
                <DrawerHeader className="flex-shrink-0 pt-6">
                  <DrawerTitle>Request New App</DrawerTitle>
                  <DrawerDescription>Search and select an app to request access</DrawerDescription>
                </DrawerHeader>
                <div className="px-4 pt-2 space-y-3 flex-1 overflow-hidden flex flex-col">
                  {/* Search Input */}
                  <div className="relative flex-shrink-0">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4 z-10" />
                    <Input
                      type="text"
                      placeholder="Search for apps..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 border-border bg-background focus-visible:ring-1 focus-visible:ring-primary"
                    />
                  </div>

                  {/* Search Results */}
                  <ScrollArea className="flex-1 min-h-0">
                    <div className="space-y-2 pb-4">
                      {isSearching ? (
                        <div className="flex items-center justify-center py-8">
                          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                        </div>
                      ) : searchError ? (
                        <div className="text-center py-8 text-destructive text-sm">
                          {searchError}
                        </div>
                      ) : searchQuery.trim() && appSearchResults.length === 0 ? (
                        <div className="text-center py-8 text-muted-foreground text-sm">
                          No apps found
                        </div>
                      ) : appSearchResults.length > 0 ? (
                        appSearchResults.map((app) => (
                          <div
                            key={app.trackId}
                            onClick={() => setSelectedApp(app)}
                            className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                              selectedApp?.trackId === app.trackId
                                ? 'bg-primary/10 border-primary'
                                : 'hover:bg-muted/50'
                            }`}
                          >
                            <img
                              src={app.artworkUrl100}
                              alt={app.trackName}
                              className="w-12 h-12 rounded-lg"
                            />
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm truncate">{app.trackName}</p>
                              <p className="text-xs text-muted-foreground truncate">{app.artistName}</p>
                              {app.primaryGenreName && (
                                <Badge variant="outline" className="mt-1 text-xs">
                                  {app.primaryGenreName}
                                </Badge>
                              )}
                            </div>
                            {selectedApp?.trackId === app.trackId && (
                              <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0" />
                            )}
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-8 text-muted-foreground text-sm">
                          Start typing to search for apps
                        </div>
                      )}
                    </div>
                  </ScrollArea>
                </div>
                <DrawerFooter className="flex-row gap-2 pt-4 pb-8 flex-shrink-0 bg-background border-t safe-area-bottom">
                  <Button onClick={handleSubmitRequest} disabled={!selectedApp} size="sm" className="flex-1">
                    Submit Request
                  </Button>
                  <Button variant="outline" onClick={() => setIsDrawerOpen(false)} size="sm" className="flex-1">
                    Cancel
                  </Button>
                </DrawerFooter>
              </DrawerContent>
            </Drawer>

      {/* App Requests List */}
            <div className="space-y-3">
              <h2 className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide px-1">Your Requests</h2>
              {hasDeviceError ? (
                <div className="text-center py-8 space-y-2 border rounded-lg bg-background">
                  <p className="text-muted-foreground text-sm">Device not found in system</p>
                  <p className="text-xs text-muted-foreground">Please contact your administrator</p>
                </div>
              ) : appRequestsData === undefined ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="flex items-center gap-3 p-3 border rounded-lg bg-background">
                      <Skeleton className="w-12 h-12 rounded-lg" />
                      <div className="flex-1 space-y-2">
                        <Skeleton className="h-4 w-32" />
                        <Skeleton className="h-3 w-24" />
                      </div>
                      <Skeleton className="h-5 w-16" />
                    </div>
                  ))}
                </div>
              ) : appRequests.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground text-sm border rounded-lg bg-background">
                  No requests yet
                </div>
              ) : (
                <div className="space-y-2">
                  {appRequests.map((request: any) => (
                    <div key={request._id} className="flex items-center gap-3 p-3 border rounded-lg bg-background hover:bg-muted/50 transition-colors">
                      {request.iconUrl && (
                        <img 
                          src={request.iconUrl} 
                          alt={request.name || 'App'} 
                          className="w-12 h-12 rounded-lg"
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{request.name || request.app}</p>
                        <p className="text-xs text-muted-foreground">
                          {formatRequestDate(request) || 'Requested'}
                        </p>
                      </div>
                      <Badge variant={getStatusBadgeVariant(request.response)}>
                        {request.response || 'pending'}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Web Request Tab */}
          <TabsContent value="web" className="space-y-4">
            {/* New Request Button at Top */}
            <Drawer open={isWebDrawerOpen} onOpenChange={setIsWebDrawerOpen}>
              <DrawerTrigger asChild>
                <Button className="w-full gap-2">
                  <Plus className="w-4 h-4" />
                  New Web Request
                </Button>
              </DrawerTrigger>
              <DrawerContent className="flex flex-col">
                <DrawerHeader className="flex-shrink-0 pt-6">
                  <DrawerTitle>Request Website Access</DrawerTitle>
                  <DrawerDescription>Enter the website URL you need access to</DrawerDescription>
                </DrawerHeader>
                <div className="px-4 pt-2 space-y-4 pb-4">
                  {/* Website URL Input */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Website</label>
                    <Input
                      type="text"
                      placeholder="example.com"
                      value={websiteUrl}
                      onChange={(e) => setWebsiteUrl(stripProtocol(e.target.value))}
                      className="border-border bg-background focus-visible:ring-1 focus-visible:ring-primary"
                    />
                  </div>

                  {/* Reason/Message Input */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Reason (Optional)</label>
                    <Input
                      placeholder="Why do you need access to this website?"
                      value={websiteReason}
                      onChange={(e) => setWebsiteReason(e.target.value)}
                      className="border-border bg-background focus-visible:ring-1 focus-visible:ring-primary"
                    />
                  </div>
                </div>
                <DrawerFooter className="flex-row gap-2 pt-4 flex-shrink-0 bg-background border-t">
                  <Button onClick={handleSubmitWebRequest} disabled={!websiteUrl.trim()} size="sm" className="flex-1">
                    Submit Request
                  </Button>
                  <Button variant="outline" onClick={() => setIsWebDrawerOpen(false)} size="sm" className="flex-1">
                    Cancel
                  </Button>
                </DrawerFooter>
              </DrawerContent>
            </Drawer>

            {/* Placeholder for web requests list */}
            <div className="text-center py-8 text-muted-foreground text-sm border rounded-lg bg-background">
              Web request history coming soon
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Requests;
